import { useEffect, useRef, useCallback } from "react"
import {
  runSimulation,
  DefaultConfig,
  type Controller,
  type TelemetryPoint,
  type LandingResult,
  type SimulationConfig,
} from "@/lib/physics"
import { HandRuleController, DefaultHandRuleConfig } from "@/lib/physics/controllers/hand-rule"
import { PIDController, DefaultPIDConfig } from "@/lib/physics/controllers/pid"
import { PPOController } from "@/lib/physics/controllers/ppo-interface"
import { useAppStore } from "./useAppStore"

const SIM_TICK_MS = 100 // real-time ms between telemetry playback steps (at 1x speed)

/**
 * Map legacy algorithm names to new engine controllers.
 * PPO algorithms are not implemented — the PPOController throws on compute(),
 * which is caught and surfaced as a simulation error in the UI.
 */
function createController(
  algorithm: string,
  rocket: NonNull<typeof DefaultConfig["rocket"]>
): Controller {
  switch (algorithm) {
    case "hand_rule":
      return new HandRuleController(rocket, DefaultHandRuleConfig)
    case "pid":
      return new PIDController(rocket, DefaultPIDConfig)
    case "ppo_extended":
    case "ppo_basic":
    case "random":
    default:
      return new PPOController()
  }
}

type NonNull<T> = T extends null | undefined ? never : T

export function useSimulation() {
  const {
    simStatus,
    setSimStatus,
    setSimTelemetry,
    setSimCurrent,
    setSimMetrics,
    setSimLogs,
    addSimLog,
    setSimDuration,
    simReset,
  } = useAppStore()

  const frameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)
  const pausedAtRef = useRef<number>(0)
  const resultRef = useRef<TelemetryPoint[]>([])
  const landingRef = useRef<LandingResult | null>(null)
  const configRef = useRef<SimulationConfig | null>(null)
  const playbackSpeedRef = useRef<number>(1)

  // Start a fresh simulation using the new physics engine.
  const start = useCallback(
    (config: SimulationConfig, algorithm: string = "hand_rule", seed: number = Date.now() % 10000) => {
      if (frameRef.current != null) return

      configRef.current = config

      let controller: Controller
      try {
        controller = createController(algorithm, config.rocket)
      } catch (err) {
        addSimLog(`Controller error: ${(err as Error).message}`)
        setSimStatus("idle")
        return
      }

      let result: ReturnType<typeof runSimulation>
      try {
        result = runSimulation(config, controller)
      } catch (err) {
        addSimLog(`Simulation error: ${(err as Error).message}`)
        setSimStatus("idle")
        return
      }

      resultRef.current = result.telemetry as TelemetryPoint[]
      landingRef.current = result.landing

      // Convert new-engine telemetry to legacy format expected by UI components.
      const legacyPoints = result.telemetry.map((p) => ({
        t: p.t,
        x: p.x,
        y: 0, // 2D simulation: no lateral (Y) movement
        z: p.altitude,
        vx: p.vx,
        vy: 0,
        vz: p.vz,
        altitude: p.altitude,
        speed: p.speed,
        fuel: p.fuel,
        throttle: p.throttle,
        tilt: p.theta,
      }))

      const finalState = result.finalState
      const fuelUsed = DefaultConfig.rocket.maxFuel - (finalState?.fuel ?? 0)
      const legacyMetrics = {
        landingSuccess: result.landing.landingSuccess,
        touchdownVelocity: result.landing.touchdownSpeed,
        horizontalError: result.landing.horizontalError,
        fuelUsed,
        fuelRemaining: finalState?.fuel ?? 0,
        maxAltitude: Math.max(...result.telemetry.map((p) => p.altitude)),
        duration: result.landing.impactTime ?? result.telemetry[result.telemetry.length - 1]?.t ?? 0,
      }

      setSimMetrics(legacyMetrics)
      setSimLogs([`Simulation started · ${algorithm} · seed ${seed}`])
      setSimTelemetry(legacyPoints)
      setSimCurrent(legacyPoints[0] ?? null)
      setSimDuration(legacyPoints[0]?.t ?? 0)
      setSimStatus("running")
      addSimLog(`Simulation started · ${algorithm} · seed ${seed}`)
      startTimeRef.current = performance.now()
      pausedAtRef.current = 0
      loop()
    },
    [setSimMetrics, setSimLogs, setSimTelemetry, setSimCurrent, setSimDuration, setSimStatus, addSimLog]
  )

  // Main playback loop: advances one telemetry point per SIM_TICK_MS real ms (adjusted by playback speed).
  // Reads simStatus directly from the Zustand store to avoid stale-closure issues where the render-time
  // simStatus value (e.g. "idle" during start()) shadows the store-updated value.
  const loop = useCallback(() => {
    if (useAppStore.getState().simStatus !== "running") return
    const now = performance.now()
    const elapsed = pausedAtRef.current > 0
      ? pausedAtRef.current - startTimeRef.current
      : now - startTimeRef.current
    const stepMs = SIM_TICK_MS / Math.max(playbackSpeedRef.current, 0.1)
    const index = Math.floor(elapsed / stepMs)
    const pts = resultRef.current
    if (index < pts.length) {
      const pt = pts[index]
      setSimCurrent(pt)
      setSimDuration(pt.t)
      frameRef.current = requestAnimationFrame(loop)
    } else {
      // Simulation finished.
      setSimStatus("completed")
      addSimLog(
        `Simulation complete · ${landingRef.current?.landingSuccess ? "LANDED" : "IMPACT"} · v=${landingRef.current?.touchdownSpeed?.toFixed(1) ?? "?"} m/s`
      )
      frameRef.current = null
    }
  }, [setSimCurrent, setSimDuration, setSimStatus, addSimLog])

  const pause = useCallback(() => {
    if (simStatus === "running") {
      pausedAtRef.current = performance.now()
      setSimStatus("paused")
      addSimLog("Simulation paused")
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [simStatus, setSimStatus, addSimLog])

  const resume = useCallback(() => {
    if (simStatus === "paused") {
      const pausedFor = performance.now() - pausedAtRef.current
      startTimeRef.current += pausedFor
      pausedAtRef.current = 0
      setSimStatus("running")
      addSimLog("Simulation resumed")
      loop()
    }
  }, [simStatus, setSimStatus, addSimLog, loop])

  const reset = useCallback(() => {
    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    startTimeRef.current = 0
    pausedAtRef.current = 0
    resultRef.current = []
    landingRef.current = null
    configRef.current = null
    playbackSpeedRef.current = 1
    simReset()
  }, [simReset])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current)
    }
  }, [])

  return {
    start,
    pause,
    resume,
    reset,
    isRunning: simStatus === "running",
    isPaused: simStatus === "paused",
    isIdle: simStatus === "idle",
    isCompleted: simStatus === "completed",
    getTelemetry: () => resultRef.current,
    getLanding: () => landingRef.current,
    getConfig: () => configRef.current,
    setPlaybackSpeed: (speed: number) => {
      playbackSpeedRef.current = speed
    },
    getPlaybackSpeed: () => playbackSpeedRef.current,
  }
}
